import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, ShieldCheck, ArrowRight, ShieldX, Clock, Beaker, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { AppState, AppAction, UserProfile } from '@/types';
import { BootScreen } from '@/components/BootScreen';

// IMPORTANT: This version must match the one checked in App.tsx gates
export const CURRENT_DISCLAIMER_VERSION = '1.0';

interface DisclaimerPageProps { 
  state: AppState; 
  dispatch: React.Dispatch<AppAction>;
}

/**
 * 🛡️ DisclaimerPage Gate
 * The final compliance check. Notifies users of the prototype nature 
 * of the OS and ensures they accept the risk before the Dashboard boots.
 */
export const DisclaimerPage: React.FC<DisclaimerPageProps> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const [hasAccepted, setHasAccepted] = useState(false);
  const [showBoot, setShowBoot] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Use the consolidated UserProfile type
  const currentUser = state.currentUser as UserProfile | null;

  /**
   * 🛰️ Final Protocol Handshake
   * Updates the user profile to clear the App.tsx gate and triggers the boot theater.
   */
  const handleAccept = async () => {
    if (!currentUser?.id || !hasAccepted || isSyncing) return;
    
    setIsSyncing(true);

    try {
      // 🛡️ PHASE 1: PERSISTENCE
      const userRef = doc(db, "users", currentUser.id);
      await updateDoc(userRef, {
        acceptedDisclaimerVersion: CURRENT_DISCLAIMER_VERSION,
        disclaimerAcceptedAt: serverTimestamp() 
      });

      dispatch({
        type: 'UPDATE_USER',
        payload: {
          acceptedDisclaimerVersion: CURRENT_DISCLAIMER_VERSION, // Explicitly update this field
          disclaimerAcceptedAt: new Date().toISOString(), // Use JS Date for local state sync
        } as Partial<UserProfile>
      });
      
      // 🖥️ PHASE 2: THEATER & STATE SYNC
      // We trigger the BootScreen while the onSnapshot in App.tsx catches the change.
      setShowBoot(true);
      
    } catch (error: any) {
      console.error("Critical disclaimer update failure:", error);
      setIsSyncing(false);
      setShowBoot(false);
      
      alert(t('disclaimer.error_handshake', 'Initialization error. Please check your connection.'));
    }
  };

  /**
   * 🛡️ RENDER GUARD
   * If the update is successful, we show the BootScreen to bridge the 
   * data propagation delay from Firestore.
   */
  if (showBoot) {
    return <BootScreen />;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col items-center justify-center p-6 animate-in fade-in duration-500 overflow-hidden relative">
      
      {/* Background Ambience */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-rose-500/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Brand Header */}
      <div className="mb-10 flex items-center gap-2 font-black text-3xl tracking-tighter text-indigo-500 dark:text-indigo-400 z-10">
        <ShieldCheck className="w-10 h-10" />
        <span>Everyday<span className="text-slate-950 dark:text-gray-200">OS</span></span>
      </div>

      <div className="max-w-2xl w-full bg-white dark:bg-slate-900 rounded-[3rem] border-2 border-slate-100 dark:border-slate-800 shadow-2xl overflow-hidden z-10">
        
        {/* --- WARNING HEADER --- */}
        <div className="bg-rose-500 dark:bg-rose-600 p-8 text-white flex items-center gap-5">
          <div className="p-3 bg-white/20 backdrop-blur-md rounded-2xl">
            <ShieldAlert className="w-8 h-8 shrink-0" />
          </div>
          <div>
            <h1 className="text-2xl font-black uppercase tracking-tight leading-none">
              {t('disclaimer.full_title', 'Usage Disclaimer')}
            </h1>
            <p className="text-rose-100 text-[10px] font-bold uppercase tracking-widest opacity-80 mt-2">
              {t('disclaimer.version_label', { version: CURRENT_DISCLAIMER_VERSION })}
            </p>
          </div>
        </div>

        <div className="p-8 lg:p-10 space-y-8">
          <div className="grid gap-6">
            {/* Prototype Section */}
            <section className="flex gap-4 p-4 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <div className="p-2 bg-rose-50 dark:bg-rose-900/30 rounded-lg h-fit">
                <Beaker className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <h3 className="font-black text-slate-950 dark:text-gray-100 uppercase text-xs mb-1 tracking-wider">
                  {t('disclaimer.prototype_title', 'Prototype Environment')}
                </h3>
                <p className="text-sm text-slate-500 dark:text-gray-400 leading-relaxed font-medium">
                  {t('disclaimer.prototype_body', 'This OS is in active development. Features, UI, and logic may change without notice.')}
                </p>
              </div>
            </section>

            {/* Volatility Section */}
            <section className="flex gap-4 p-4 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <div className="p-2 bg-rose-50 dark:bg-rose-900/30 rounded-lg h-fit">
                <Clock className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <h3 className="font-black text-slate-950 dark:text-gray-100 uppercase text-xs mb-1 tracking-wider">
                  {t('disclaimer.volatility_title', 'Data Volatility')}
                </h3>
                <p className="text-sm text-slate-500 dark:text-gray-400 leading-relaxed font-medium">
                  {t('disclaimer.volatility_body', 'While we strive for stability, schema migrations may occasionally occur during the beta period.')}
                </p>
              </div>
            </section>

            {/* Security Section */}
            <section className="flex gap-4 p-4 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <div className="p-2 bg-rose-50 dark:bg-rose-900/30 rounded-lg h-fit">
                <ShieldX className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <h3 className="font-black text-slate-950 dark:text-gray-100 uppercase text-xs mb-1 tracking-wider">
                  {t('disclaimer.pii_title', 'Privacy & Security')}
                </h3>
                <p className="text-sm text-slate-500 dark:text-gray-400 leading-relaxed font-medium">
                  {t('disclaimer.pii_body', 'Your data is encrypted, but do not store sensitive high-risk items like plaintext passwords.')}
                </p>
              </div>
            </section>
          </div>

          {/* Consent Checkbox */}
          <div className={`p-6 rounded-2xl border-2 transition-all ${
            hasAccepted 
            ? 'border-indigo-500 bg-indigo-500/5 dark:bg-indigo-500/10' 
            : 'bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700'
          }`}>
            <label className="flex items-start gap-4 cursor-pointer group">
              <input 
                type="checkbox"
                checked={hasAccepted}
                onChange={(e) => setHasAccepted(e.target.checked)}
                className="w-6 h-6 mt-0.5 rounded border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-indigo-500 focus:ring-indigo-500 transition-all cursor-pointer"
              />
              <span className="text-sm font-bold text-slate-700 dark:text-gray-200 leading-snug">
                {t('disclaimer.checkbox_label', 'I understand this is a beta environment and accept the risk.')}
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <Button 
            onClick={handleAccept}
            disabled={!hasAccepted || isSyncing}
            className="w-full h-16 rounded-2xl text-lg font-black gap-3 bg-indigo-600 hover:bg-indigo-700 text-white transition-all shadow-xl active:scale-[0.98]"
          >
            {isSyncing ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                {t('disclaimer.cta_dashboard', 'Initialize Dashboard')}
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </Button>
        </div>
      </div>
      
      <p className="mt-8 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-gray-500">
        {t('disclaimer.footer_tagline', 'EverydayOS Security Protocol')}
      </p>
    </div>
  );
};